import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { refreshSession, setAuthLostHandler } from '@/api/client'
import { useAuthStore } from '@/store/authStore'
import { useSystemCan } from '@/hooks/usePermissions'
import type { SystemPermission } from '../../../../packages/shared/src/permissions'
import { BrandMark } from '@/components/common/BrandMark'
import { tr } from '@/i18n/tr'
import { Dots, notifications } from '@/ui'

/**
 * Oturum kurtarma ve koruma.
 *
 * <p>Access token yalnızca bellekte tutulduğu için sayfa yenilendiğinde
 * kaybolur. Burada HttpOnly refresh cookie'si ile sessizce yeni bir token
 * alınır; başarısız olursa kullanıcı giriş ekranına yönlenir.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, status, setSession, clear } = useAuthStore()
  const [booting, setBooting] = useState(status === 'unknown')
  const location = useLocation()

  useEffect(() => {
    // Backend "hesabın kapatıldı" derse kullanıcı bunu bir istek içinde öğrenir.
    setAuthLostHandler((reason) => {
      clear()
      notifications.show({
        color: 'red',
        title: reason === 'disabled' ? 'Hesabınız devre dışı' : 'Oturum sonlandı',
        message:
          reason === 'disabled'
            ? 'Hesabınız yönetici tarafından devre dışı bırakıldı.'
            : 'Oturumunuzun süresi doldu. Lütfen tekrar giriş yapın.',
        autoClose: 8000,
      })
    })
    return () => setAuthLostHandler(null)
  }, [clear])

  useEffect(() => {
    if (status !== 'unknown') {
      setBooting(false)
      return
    }
    let cancelled = false
    // Tek uçuş korumalı yenileme: StrictMode'un effect'i iki kez çalıştırması
    // ya da aynı anda 401 alan istekler ikinci bir /refresh doğurmamalı.
    refreshSession()
      .then((res) => {
        if (!cancelled) setSession(res.accessToken, res.user)
      })
      .catch(() => {
        if (!cancelled) clear()
      })
      .finally(() => {
        if (!cancelled) setBooting(false)
      })
    return () => {
      cancelled = true
    }
  }, [status, setSession, clear])

  if (booting) {
    // Oturum kurtarılırken marka görünsün; boş bir yükleme dairesi
    // uygulamanın çöktüğü izlenimi veriyordu.
    return (
      <div role="status" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 18, fontWeight: 600, letterSpacing: '-0.045em', textTransform: 'lowercase' }}>
          <BrandMark />
          {tr.app.name}
        </div>
        <Dots />
        <span style={{ fontSize: 14, color: 'var(--nx-fg-muted)' }}>{tr.common.loading}</span>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }

  // Yönetici tarafından açılan hesaplar önce parola değiştirmek zorunda.
  if (user.mustChangePassword && location.pathname !== '/parola-degistir') {
    return <Navigate to="/parola-degistir" replace />
  }

  return <>{children}</>
}

/** Sistem izni ister (yönetim ekranları); yoksa ana sayfaya döner. Sunucu aynı izni ayrıca uygular. */
export function RequireSystem({ permission, children }: { permission: SystemPermission; children: React.ReactNode }) {
  const can = useSystemCan()
  if (!can(permission)) return <Navigate to="/" replace />
  return <>{children}</>
}
